<script lang="ts">
  import { onMount } from 'svelte';
  import type { HomepageHardwareScrollV2Props } from './types';
  import layoutHelpers from '$lib/helpers/layout-helpers';

  // #region Props
  export let imgSequenceConfig: HomepageHardwareScrollV2Props['imgSequenceConfig'];
  export let fallbackImgUpgradeDelay: HomepageHardwareScrollV2Props['fallbackImgUpgradeDelay'];

  export let windowHeight: number;
  export let y: number;
  export let section: number;

  export let isMobile: boolean;
  // #endregion

  // #region Setup
  const imgName = {
    ext: 'webp',
    prefix: 'frame-',
    prefixHq: 'frame-',
  };
  let canvas: HTMLCanvasElement;
  let ctx: CanvasRenderingContext2D;
  let lastFrame: string = '';
  let imgSequence: HTMLImageElement[][] | null[][] = [];
  let imgSequenceHq: HTMLImageElement[][] | null[][] = [];
  let upgradeImageTimeout: NodeJS.Timeout;
  let observer: IntersectionObserver;

  let w: number;
  let h: number;
  let isVisible = false;
  $: sectionProgress = Math.max((y - windowHeight) / windowHeight - section, 0);
  // #endregion

  // #region Fallback
  const initFallback = async () => {
    if (!canvas) {
      return;
    }
    ctx = canvas.getContext('2d') as CanvasRenderingContext2D;

    handleResize();

    loadImageSequence(section);
  };

  const getImgNumber = (section: number) => Math.floor(sectionProgress * imgSequence[section].length);

  const loadImageSequence = (loadSection: number) => {
    if (!Object.keys(imgSequenceConfig)?.length) {
      return;
    }
    if (imgSequence[loadSection]?.length) {
      return;
    }

    // wrap in a brief timeout in case to account for scroll in the section
    setTimeout(async () => {
      // fill the array with empties
      if (imgSequence.length !== Object.keys(imgSequenceConfig).length) {
        imgSequence = Array.from({ length: Object.keys(imgSequenceConfig).length }, () => []);
        imgSequenceHq = Array.from({ length: Object.keys(imgSequenceConfig).length }, () => []);
      }

      const { url, n } = imgSequenceConfig[loadSection];
      // add the same number of empty spots for the both image arrays
      imgSequence[Number(loadSection)] = Array.from({ length: n }, () => null);
      imgSequenceHq[Number(loadSection)] = Array.from({ length: n }, () => null);

      const preLoadImg = (loadSection: number, nImg: number) => {
        const img = new Image();
        img.src = `${url}/${imgName.prefix}${nImg}.${imgName.ext}?cache=1`;
        if (img.complete) {
          imgSequence[Number(loadSection)][nImg] = img;
        } else {
          img.onload = () => {
            imgSequence[Number(loadSection)][nImg] = img;
          };

          img.onerror = (e: any) => console.error('Failed to load fallback image:', e.target);
        }
      };

      // queue the load starting from the current image
      const nImg = loadSection === section ? getImgNumber(loadSection) : 0;
      for (let i = 0; i < Math.ceil(n / 2); i++) {
        preLoadImg(loadSection, (nImg + i) % n);
        preLoadImg(loadSection, (nImg - i + n - 1) % n);
      }

      // wait for the image to load
      await imgSequence[loadSection][nImg]?.decode();

      // redraw the canvas once the image has loaded

      draw(false);
    }, 100);
  };
  // load neighboring sections reactively
  $: (() => {
    if (!canvas) {
      return;
    }
    const indecies = [0, 1, -1]; // always load this section first
    for (const i of indecies) {
      if (section + i < 0 || section + i >= Object.keys(imgSequenceConfig).length) {
        continue;
      }
      loadImageSequence(section + i);
    }
  })();

  const upgradeImage = (section: number, nImg: number) => {
    clearTimeout(upgradeImageTimeout);

    if (imgSequenceHq[section][nImg]) {
      return imgSequenceHq[section][nImg];
    }

    upgradeImageTimeout = setTimeout(async () => {
      // scope the variables
      const _section = section;
      const _sectionProgress = sectionProgress;
      const _nImg = nImg;

      const img = new Image();
      img.src = `${imgSequenceConfig[_section].url}/${imgName.prefixHq}${_nImg}.${imgName.ext}`;

      // wait for the image to load
      await img.decode();
      imgSequenceHq[_section][_nImg] = img;

      // only draw if the image is still the same
      if (_section === section && _nImg === getImgNumber(section)) {
        draw(false, { section: _section, sectionProgress: _sectionProgress, nImg: _nImg });
      }
    }, fallbackImgUpgradeDelay);
    return null;
  };

  const draw = (
    isCheckFrameForRedraw = true,
    override: { section?: number; sectionProgress?: number; nImg?: number } = {}
  ) => {
    if (!ctx) return;
    if (!imgSequence[section]?.length) return;

    // save the current values or use the override to prevent flickering
    const _section = override?.section ?? section;
    const _sectionProgress = override?.sectionProgress ?? sectionProgress;
    const _nImg = override?.nImg ?? getImgNumber(_section);
    const img = imgSequence[_section][_nImg] as HTMLImageElement;

    if (!img) return;

    // only render if the frame has changed
    const getThisFrame = () => `${_section},${_nImg}`;
    if (isCheckFrameForRedraw && lastFrame === getThisFrame()) {
      return;
    }
    lastFrame = getThisFrame();

    const { xPosition, yPosition, imgWidth, imgHeight } = calculateCanvasImageDimensions(
      img,
      _section,
      _sectionProgress
    );
    const imgHq = upgradeImage(_section, _nImg);
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(imgHq ?? img, xPosition, yPosition, imgWidth, imgHeight);
  };

  const calculateCanvasImageDimensions = (img: HTMLImageElement, _section: number, _sectionProgress: number) => {
    const imgRatio = img.naturalWidth / img.naturalHeight;
    const verticalOffset = 0;
    const stickThreshold = window.innerWidth < layoutHelpers.BREAKPOINT_WIDTHS.small.minWidth ? 0.05 : 0;
    let scaleFactor = 1;

    let imgHeight, imgWidth;

    if (window.innerWidth < layoutHelpers.BREAKPOINT_WIDTHS.small.minWidth) {
      // minimum width/height for the image on mobile
      const minViewportWidthRatio = 0.6;
      const minViewportHeightRatio = 0.6;

      const minDrawWidth = w * minViewportWidthRatio;
      const minDrawHeight = h * minViewportHeightRatio;

      if (w / h > imgRatio) {
        imgHeight = h;
        imgWidth = imgHeight * imgRatio;
      } else {
        imgWidth = w;
        imgHeight = imgWidth / imgRatio;
      }

      // minimum width & height constraints
      if (imgWidth < minDrawWidth) {
        imgWidth = minDrawWidth;
        imgHeight = minDrawWidth / imgRatio;
      } else if (imgHeight < minDrawHeight) {
        imgHeight = minDrawHeight;
        imgWidth = minDrawHeight * imgRatio;
      }
    } else {
      // Portrait tablet
      if (
        window.innerWidth < layoutHelpers.BREAKPOINT_WIDTHS.large.minWidth &&
        window.innerWidth < window.innerHeight
      ) {
        scaleFactor = 1.25;
      }
      // on desktop, use the natural width and adjust to fit the viewport
      if (img.naturalWidth <= w && img.naturalHeight <= h) {
        // use the natural size times the scaling factor
        imgWidth = img.naturalWidth * scaleFactor;
        imgHeight = img.naturalHeight * scaleFactor;
      } else if (w / h > imgRatio) {
        // fit by height
        imgHeight = h * scaleFactor;
        imgWidth = imgHeight * imgRatio;
      } else {
        // fit by width
        imgWidth = w * scaleFactor; // Apply scaling factor
        imgHeight = imgWidth / imgRatio;
      }
    }

    let yPosition;

    if (_section === 0 && _sectionProgress < stickThreshold) {
      // stick to bottom and move towards center
      yPosition = h - imgHeight;
    } else if (_section === 0 && _sectionProgress >= stickThreshold) {
      const adjustedProgress = (_sectionProgress - stickThreshold) / (1 - stickThreshold); // lerp the progress
      const startY = h - imgHeight; // bottom
      const endY =
        (h - imgHeight) / 2 + (window.innerWidth < layoutHelpers.BREAKPOINT_WIDTHS.small.minWidth ? verticalOffset : 0); // center plus offset
      yPosition = startY - (startY - endY) * Math.min(adjustedProgress, 1);
    } else {
      yPosition =
        (h - imgHeight) / 2 + (window.innerWidth < layoutHelpers.BREAKPOINT_WIDTHS.small.minWidth ? verticalOffset : 0);
    }

    const xPosition = (w - imgWidth) / 2; // centered

    return { imgWidth, imgHeight, xPosition, yPosition };
  };
  // #endregion

  // #region Handlers
  const handleResize = () => {
    if (!ctx) {
      return;
    }

    ctx.canvas.width = window.innerWidth;
    ctx.canvas.height = window.innerHeight;
    w = ctx.canvas.width;
    h = ctx.canvas.height;

    draw(false);
  };

  const handleIntersectionObserver = (entries: IntersectionObserverEntry[]): void => {
    const [entry] = entries;
    isVisible = entry.isIntersecting;
  };

  const handleScroll = () => {
    if (isVisible) draw();
  };
  // #endregion

  // #region Lifecycle
  onMount(() => {
    observer = new IntersectionObserver(handleIntersectionObserver, {
      threshold: 0,
    });

    observer.observe(canvas);

    initFallback();
  });
  // #endregion
</script>

<svelte:window on:resize={handleResize} on:scroll={handleScroll} />
<canvas class="img-sequence section-{section}" bind:this={canvas} />

<style lang="scss">
  @use '@squareup/public-web-design-system/dist/styles/xms-fe/core/config/_media_queries' as mediaQueries;

  @layer custom-components {
    .img-sequence {
      height: 100%;
      width: 100%;
    }

    @media (min-aspect-ratio: 8/5) {
      @include mediaQueries.until('small') {
        .img-sequence.section-5 {
          transform: scale(0.85);
          transform-origin: left bottom;
        }
      }
    }
  }
</style>
